import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { MonthYearValue } from '../../../../utils/MonthYear/MonthYearValue';
import { MonthYear } from '../../../../utils/MonthYear/monthYear';
import { filterData } from './MultiInputComponentsUtils';
import { sumOfMonthYearValue } from '../../../../utils/MonthYear/MonthYearValue';
import { InvalidValueErrorMessage } from './MultiInputChartComponents/InvalidValueErrorMessage';
import { MultiChartCustomLegend } from './MultiInputChartComponents/MultiChartCustomLegend';
import { ChartInputEditFields } from './MultiInputChartComponents/ChartInputEditFields';
import { getMultiChartOptions, getMuiltiChartData as getMultiChartData } from './MultiInputComponentsUtils';
import { ScrollDataView } from './MultiInputChartComponents/ScrollDataView';
import { getDateAfter, isBefore } from '../../../../pages/FutureOrder/futureOrderUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

export const DEFAULT_COLOR = '#F3A1B8';

export interface IChartData {
  data: MonthYearValue[];
  title: string;
  type: 'bar' | 'line';
  color?: string;
  transparent?: boolean;
  isFlipped?: boolean;
  barThickness?: number;
}

interface DateValueChartProps {
  mainChart: IChartData;
  numberOfMonthsToShow: number;
  changeMonths: number;
  startingDate: MonthYear;
  endDate?: MonthYear;
  goForward?: () => void;
  goBack?: () => void;
  additionalCharts?: IChartData[];
  updateEditablePropertyFunc?: (_data: MonthYearValue[]) => void;
  isHideValueInput?: boolean;
  suggestedMaxValue?: number;
  suggestedMinValue?: number;
}

export type { DateValueChartProps };

const DateValueInputMultiChart: React.FC<DateValueChartProps> = ({
  mainChart,
  updateEditablePropertyFunc,
  isHideValueInput = false,
  numberOfMonthsToShow,
  startingDate,
  endDate,
  goBack = () => 0,
  goForward = () => 0,
  additionalCharts,
  suggestedMaxValue,
  suggestedMinValue,
}) => {
  const { data: mainChartData, title: mainChartTitle, color: mainChartColor } = mainChart;
  const [editableChartData, setEditableChartData] = useState<MonthYearValue[]>(mainChartData);
  const [isChangeableValueStable, setIsChangeableValueStable] = useState(true);
  const totalQuantity = sumOfMonthYearValue(mainChartData);

  useEffect(() => {
    setEditableChartData(mainChartData);
  }, [mainChart]);

  useEffect(() => {
    sumOfMonthYearValue(editableChartData) === totalQuantity &&
      updateEditablePropertyFunc &&
      updateEditablePropertyFunc(editableChartData);
  }, [editableChartData]);

  useEffect(() => {
    sumOfMonthYearValue(editableChartData) !== totalQuantity
      ? setIsChangeableValueStable(false)
      : setIsChangeableValueStable(true);
  }, [editableChartData]);

  const showGoLeft = (startingDate: MonthYear, mainData: MonthYearValue[]): boolean => {
    return !!mainData?.[0]?.date && isBefore(mainData[0].date, startingDate);
  };

  const showGoRight = (startingDate: MonthYear, showMonths: number, dataEndDate?: MonthYear): boolean => {
    return !!dataEndDate && isBefore(getDateAfter(startingDate, showMonths), dataEndDate);
  };

  return (
    <div className="flex flex-col flex-1 gap-1">
      <div className="flex flex-col flex-1">
        <div className="relative flex-1 h-64">
          {showGoLeft(startingDate, mainChart?.data) && <ScrollDataView isLeftSide onClick={goBack} />}

          <Chart
            type="bar"
            options={getMultiChartOptions(mainChart, additionalCharts, suggestedMaxValue, suggestedMinValue)}
            data={getMultiChartData(
              {
                color: mainChart.color,
                data: editableChartData,
                title: mainChart.title,
                type: mainChart.type,
                transparent: mainChart.transparent,
                isFlipped: mainChart.isFlipped,
                barThickness: mainChart.barThickness,
              },
              numberOfMonthsToShow,
              startingDate,
              additionalCharts,
            )}
          />
          {showGoRight(startingDate, numberOfMonthsToShow, endDate) && (
            <ScrollDataView isLeftSide={false} onClick={goForward} />
          )}
        </div>
        {!isHideValueInput && updateEditablePropertyFunc && mainChartData ? (
          <ChartInputEditFields
            dataSet={filterData(editableChartData, numberOfMonthsToShow, startingDate).map((item) => item.value)}
            isNumbersValid={isChangeableValueStable}
            onInputChange={(value: number, index: number) => {
              const newValue: MonthYearValue = {
                date: filterData(editableChartData, numberOfMonthsToShow, startingDate)[index].date,
                value: value,
              };
              setEditableChartData(
                editableChartData.map((item) =>
                  item.date.year === newValue.date.year && item.date.month === newValue.date.month
                    ? { ...item, value: newValue.value }
                    : item,
                ),
              );
            }}
          />
        ) : null}
      </div>

      <div className="flex">
        {!isChangeableValueStable && (
          <div className="flex justify-start">
            <InvalidValueErrorMessage differenceAmount={totalQuantity - sumOfMonthYearValue(editableChartData)} />
          </div>
        )}
        {isHideValueInput && (
          <div className="flex justify-center flex-grow">
            <MultiChartCustomLegend
              color={mainChartColor || DEFAULT_COLOR}
              title={mainChartTitle}
              additionalValues={additionalCharts?.map((item) => ({ color: item.color || '', title: item.title }))}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DateValueInputMultiChart;
